import { MapEdit, MapRow } from './map-types';

/** What painting asks of the engine. */
export interface PaintHost {
  /** Painting edits, so it only starts in an editing mode. */
  canPaint(): boolean;
  /** The suburb under a point in the map element, if any. */
  pick(x: number, y: number): string | null;
  /** One suburb's edits in the current mode, applied locally; the touched suburbs as they were go into `before`. */
  edit(code: string, before: MapRow[]): MapEdit[];
  setDraggable(on: boolean): void;
  redraw(): void;
  /** The stroke is over: one action (one save, one undo step). */
  done(edits: MapEdit[], before: MapRow[]): void;
}

/**
 * Shift-drag paints across many suburbs (#256): each suburb the pointer crosses is edited as it is reached, and the
 * stroke is reported as one action when the button comes up. A plain drag pans the map. Returns the teardown.
 */
export function wirePainting(el: HTMLElement, host: PaintHost): () => void {
  let stroke: { edits: MapEdit[]; before: MapRow[]; seen: Set<string> } | null = null;
  const at = (e: PointerEvent) => {
    if (!stroke) return;
    const r = el.getBoundingClientRect();
    const code = host.pick(e.clientX - r.left, e.clientY - r.top);
    if (!code || stroke.seen.has(code)) return;
    stroke.seen.add(code);
    const edits = host.edit(code, stroke.before);
    if (edits.length) {
      stroke.edits.push(...edits);
      host.redraw();
    }
  };
  const down = (e: PointerEvent) => {
    if (!e.shiftKey || !host.canPaint()) return;
    e.preventDefault();
    host.setDraggable(false);
    stroke = { edits: [], before: [], seen: new Set() };
    at(e);
  };
  const end = () => {
    if (!stroke) return;
    host.setDraggable(true);
    const { edits, before } = stroke;
    stroke = null;
    if (edits.length) host.done(edits, before);
  };
  el.addEventListener('pointerdown', down, true);
  el.addEventListener('pointermove', at);
  el.addEventListener('pointerup', end);
  el.addEventListener('pointerleave', end);
  return () => {
    el.removeEventListener('pointerdown', down, true);
    el.removeEventListener('pointermove', at);
    el.removeEventListener('pointerup', end);
    el.removeEventListener('pointerleave', end);
  };
}
